'use client'
import * as React from 'react'
import { createPortal } from 'react-dom'
import { AppRoutes, AppRoutesParameterized } from '@repo/web-utils/app-routes'
import { Link } from '@repo/web-ui/components/link'
import { tw } from 'utils/classnames'
import { CheckCircleIcon, ChevronRightIcon } from 'primitives/icons'
import { ArrowLeftIcon } from 'primitives/icons/arrows'
import Image from 'next/image'
import { SkeletonItem } from 'components/skeleton-item'
import { useTranslations } from 'next-intl'
import { Tooltip } from 'primitives/tooltip'
import { usePathname } from '@repo/nextjs-utils/navigation'
import { Z_INDEX_MODALS } from 'constants/modals'
import { CloseIcon, DiscussionsIcon, HomeIcon, MenuIcon, ProjectsIcon } from '../../../assets/icons'
import type { MobileMenuItemModel } from '../../_hooks/use-menu-models'
import { useMenuModels } from '../../_hooks/use-menu-models'
import { useClubhousesGetDetailsQuery } from '@web/gen/clubhouses'
import { formatString } from '../../../_utils/format-string'
import type { KnowledgeHub } from '@repo/payload-kit/payload-types'
import { useIsMounted } from 'hooks/use-is-mounted'

export function MobileNavbar({
  clubhouseSlug,
  knowledgeHubRouteConfig,
}: {
  clubhouseSlug: string
  knowledgeHubRouteConfig: KnowledgeHub | null
}) {
  const [isOpen, setIsOpen] = React.useState(false)
  const pathname = usePathname()
  const mounted = useIsMounted()

  const items = [
    {
      label: 'Home',
      icon: <HomeIcon />,
      href: `${AppRoutes.CLUBHOUSE}${clubhouseSlug}/`,
    },
    {
      label: 'Discuss',
      icon: <DiscussionsIcon />,
      href: `${AppRoutes.CLUBHOUSE}${clubhouseSlug}/discussions/`,
    },
    {
      label: 'Social',
      icon: <ProjectsIcon />,
      href: `${AppRoutes.CLUBHOUSE}${clubhouseSlug}/inspiration/projects/`,
    },
  ]

  React.useEffect(() => {
    document.body.style.setProperty('--widget-bottom', '80px')
    return () => {
      document.body.style.removeProperty('--widget-bottom')
    }
  }, [])

  if (!mounted) {
    return null
  }

  return createPortal(
    <>
      <nav
        style={{ zIndex: Z_INDEX_MODALS.default }}
        className="bg-cf-white fixed bottom-0 left-0 right-0 flex h-[64px] flex-row items-center justify-between border-t border-t-[#0A0A0B1A] px-2 py-1 text-[#4B5563] lg:hidden"
      >
        {items.map(item => (
          <Link
            key={item.label}
            href={item.href}
            className={tw(
              'rounded-2 flex w-14 flex-col items-center justify-center p-2 text-center hover:bg-[#EEEFF2] hover:text-[#131315]',
              pathname === item.href && 'bg-[#EEEFF2] text-[#131315]',
            )}
          >
            {item.icon}
            <span className="text-xs">{item.label}</span>
          </Link>
        ))}
        <button
          className="rounded-2 flex w-14 flex-col items-center justify-center p-2 text-center hover:bg-[#EEEFF2] hover:text-[#131315]"
          onClick={() => setIsOpen(!isOpen)}
        >
          <MenuIcon />
          <span className="text-xs">More</span>
        </button>
      </nav>
      <NavigationSlideUp
        clubhouseSlug={clubhouseSlug}
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        knowledgeHubRouteConfig={knowledgeHubRouteConfig}
      />
    </>,
    document.body,
  )
}

function NavigationSlideUp({
  clubhouseSlug,
  isOpen,
  onClose,
  knowledgeHubRouteConfig,
}: {
  clubhouseSlug: string
  isOpen: boolean
  onClose: () => void
  knowledgeHubRouteConfig: KnowledgeHub | null
}) {
  const { mobileMenuModel } = useMenuModels({ clubhouseSlug, knowledgeHubRouteConfig })
  const [selectedItem, setSelectedItem] = React.useState<MobileMenuItemModel | null>(null)
  return (
    <div
      className={tw(
        'bg-cf-white fixed left-0 right-0 z-50 flex h-screen flex-col p-4',
        isOpen ? 'top-0' : 'top-[100%]',
        'transition-[top] duration-300 ease-in-out',
      )}
    >
      <button className="self-end" onClick={onClose}>
        <CloseIcon />
      </button>
      <ClubhouseHeader clubhouseSlug={clubhouseSlug} />
      <div className="relative flex flex-grow flex-col gap-3">
        {mobileMenuModel
          .filter(item => item.isActive)
          .map(item => {
            return (
              <React.Fragment key={item.text}>
                <NavigationSubMenu
                  item={item}
                  selectedParentItem={selectedItem}
                  setSelectedParentItem={setSelectedItem}
                />
              </React.Fragment>
            )
          })}
      </div>
    </div>
  )
}

const NavigationSubMenu = ({
  item,
  selectedParentItem,
  setSelectedParentItem,
}: {
  item: MobileMenuItemModel
  selectedParentItem: MobileMenuItemModel | null
  setSelectedParentItem: (item: MobileMenuItemModel | null) => void
}) => {
  const [selectedItem, setSelectedItem] = React.useState<MobileMenuItemModel | null>(null)
  const Icon = item.icon
  return (
    <>
      <div
        className="flex flex-row items-center gap-2 rounded-lg p-2"
        role="button"
        onClick={() => {
          setSelectedParentItem(item)
        }}
      >
        {Icon ? <Icon /> : null}
        <span className="text-sm">{item.text}</span>
        <ChevronRightIcon className="ml-auto" />
      </div>
      <div
        className={tw(
          'bg-cf-white absolute bottom-0 left-[calc(100%+16px)] top-0 w-full  transition-transform',
          selectedParentItem?.text === item.text
            ? 'z-20 -translate-x-[calc(100%+16px)]'
            : 'translate-x-0',
        )}
      >
        <div className="flex flex-col gap-3">
          <div
            className="flex items-center gap-2 pt-6 text-[18px] font-semibold leading-[20px] text-[#432981]"
            role="button"
            onClick={() => {
              setSelectedItem(null)
              setSelectedParentItem(null)
            }}
          >
            <ArrowLeftIcon /> <span>{item.text}</span>
          </div>
          {item.items
            ?.filter(child => child.isActive)
            ?.map(child => {
              const ChildIcon = child.icon
              return child.items?.length ? (
                <NavigationSubMenu
                  key={child.text}
                  item={child}
                  selectedParentItem={selectedItem}
                  setSelectedParentItem={setSelectedItem}
                />
              ) : (
                <Link
                  key={child.text}
                  href={child.path}
                  className="flex flex-row items-center gap-2 rounded-lg p-2"
                >
                  {ChildIcon ? <ChildIcon className="size-5" /> : null}
                  <span className="text-sm">{child.text}</span>
                </Link>
              )
            })}
        </div>
      </div>
    </>
  )
}

const ClubhouseHeader = ({ clubhouseSlug }: { clubhouseSlug: string }) => {
  const t = useTranslations('pages.clubhouse')
  const { data: clubhouse, isPending } = useClubhousesGetDetailsQuery(
    {
      input: {
        clubhouseSlug,
      },
    },
    { select: data => data?.clubhousesGetDetails?.clubhouse },
  )
  return isPending ? (
    <SkeletonItem className="h-[64] w-full rounded-[12px]" />
  ) : (
    <div className="my-2 flex gap-3 rounded-[12px] bg-[#DBEFE9] bg-[linear-gradient(180deg,_#000_0%,_#0000_60%)] px-2 py-3 [background-blend-mode:overlay,normal]">
      <div
        className={tw(
          'flex h-10 w-10 items-center justify-center rounded-full border-[#0A0A0B1A] group-hover:opacity-60 group-active:opacity-80',
          clubhouse?.bgColor ? `bg-[${clubhouse?.bgColor}]` : 'bg-cf-white',
        )}
      >
        <Image src={clubhouse?.avatarImage || ''} alt="logo" width={38} height={38} />
      </div>
      <div className="pt-[1px]">
        <div className="text-16 mb-0.5 flex items-center gap-1 font-semibold leading-[18px]">
          <Link href={`${AppRoutes.CLUBHOUSE}${clubhouseSlug}`}>
            {clubhouse?.name || clubhouseSlug} {t('sidebar.title')}
          </Link>
          <Tooltip
            value={
              <div className="text-cf-white bg-cf-black text-12 rounded-2 max-w-[200px] p-3 text-center shadow-md">
                {t('sidebar.tooltip')}
              </div>
            }
            arrowClassName="fill-cf-black"
            delay={0}
            side="bottom"
            hideArrow={false}
            className="p-0"
            avoidCollisions
          >
            <div className="size-3">
              <CheckCircleIcon className="text-cf-cricut-green size-3 " />
            </div>
          </Tooltip>
        </div>

        <Link
          href={`${AppRoutesParameterized.CLUBHOUSE_SEARCH_RESULTS(clubhouse?.type)}`}
          className="text-14 text-gray-200"
        >
          {formatString(clubhouse?.type)}
        </Link>
      </div>
    </div>
  )
}
